import { FILTER_ABSENT } from '../../filter/services/filter-dsl.compiler';
import type { IFlowNodeContext } from '../interfaces/node-context.interface';
import {
  asText,
  parseTemplate,
  resolveContextPath,
  TEMPLATE_ROOTS,
  type IContextPath,
  type TTemplateRoot,
} from '../services/flow-template';

export const AGENT_TOOL_ROOTS: readonly TTemplateRoot[] = [...TEMPLATE_ROOTS, 'tool'];

export type TToolContext = IFlowNodeContext & { tool: Record<string, unknown> };

export function toolFieldRefs(text: string): string[] {
  try {
    return parseTemplate(text, AGENT_TOOL_ROOTS)
      .parts.flatMap((part) => (part.kind === 'ref' && part.ref.root === 'tool' ? [part.ref] : []))
      .map((ref: IContextPath) => ref.path.split('.')[1] ?? '');
  } catch {
    return [];
  }
}

// §3.3: scheme and host literal, ended before the first `{{` — the model never picks where.
export function hostIsFixed(url: string): boolean {
  const open = url.indexOf('{{');
  if (open === -1) return true;
  return /^https?:\/\/[^/?#{}]+[/?#]/i.test(url.slice(0, open));
}

function segment(value: unknown): string {
  const text = asText(value);
  if (/^\.+$/.test(text)) return text.replace(/\./g, '%2E');
  return encodeURIComponent(text);
}

export function renderToolTemplate(text: string, ctx: TToolContext, url = false): unknown {
  if (!text.includes('{{')) return text;
  const parsed = parseTemplate(text, AGENT_TOOL_ROOTS);
  if (parsed.single && !url) {
    const value = resolveContextPath(ctx, (parsed.parts[0] as { ref: IContextPath }).ref);
    return value === FILTER_ABSENT ? undefined : value;
  }
  return parsed.parts
    .map((part) => {
      if (part.kind === 'text') return part.text;
      const value = resolveContextPath(ctx, part.ref);
      if (url && part.ref.root === 'tool') return value === FILTER_ABSENT ? '' : segment(value);
      return asText(value);
    })
    .join('');
}

/** In the `urlKey` string an agent's value is one encoded path segment: no `/`, no `..`. */
export function renderToolValue(value: unknown, ctx: TToolContext, urlKey?: string): unknown {
  if (typeof value === 'string') return renderToolTemplate(value, ctx);
  if (Array.isArray(value)) return value.map((item) => renderToolValue(item, ctx));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        typeof item === 'string' && key === urlKey
          ? renderToolTemplate(item, ctx, true)
          : renderToolValue(item, ctx),
      ]),
    );
  }
  return value;
}
